
'use client';
import * as React from 'react';
import './landing-page.css';
import './features-section.css';
import Link from 'next/link';
import { useAuth } from '@/context/auth-context';
import AuthGate from '@/components/auth-gate';
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Loader2, AlertTriangle, Eye, EyeOff, Handshake, Instagram, Linkedin } from 'lucide-react';
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

const signUpSchema = z.object({
  name: z.string().min(2, "Username must be at least 2 characters."),
  email: z.string().email("Please enter a valid email address."),
  password: z.string().min(6, "Password must be at least 6 characters."),
});

type SignUpValues = z.infer<typeof signUpSchema>;

function SignUpForm() {
    const { signup, loading, error } = useAuth();
    const [showPassword, setShowPassword] = React.useState(false);
    
    const form = useForm<SignUpValues>({
        resolver: zodResolver(signUpSchema),
        defaultValues: { name: "", email: "", password: "" },
    });

    const onSubmit = async (data: SignUpValues) => {
        try {
          await signup(data.name, data.email, data.password);
        } catch (err: any) {
            // Error is handled by the useAuth hook and displayed via the `error` state
        }
    };
    
    return (
        <div className="signup-form">
            {error && (
                <Alert variant="destructive" className="mb-4 text-left">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertTitle>Sign Up Failed</AlertTitle>
                    <AlertDescription>{error}</AlertDescription>
                </Alert>
            )}
             <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                    <FormField
                        control={form.control}
                        name="name"
                        render={({ field }) => (
                        <FormItem>
                            <FormLabel>Username</FormLabel>
                            <FormControl>
                            <Input placeholder="Enter your username" {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                        <FormItem>
                            <FormLabel>Email Address</FormLabel>
                            <FormControl>
                            <Input placeholder="you@example.com" {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="password"
                        render={({ field }) => (
                        <FormItem>
                            <FormLabel>Password</FormLabel>
                            <FormControl>
                              <div className="relative">
                                <Input 
                                  type={showPassword ? "text" : "password"} 
                                  placeholder="••••••••" 
                                  {...field} 
                                />
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="absolute inset-y-0 right-0 h-full px-3"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </Button>
                              </div>
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                        )}
                    />
                    <Button type="submit" className="w-full btn-submit" size="lg" disabled={loading}>
                        {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Create Account & Start Exploring
                    </Button>
                </form>
            </Form>
        </div>
    )
}

export default function LandingPage() {
    const [loginModalOpen, setLoginModalOpen] = React.useState(false);
    const [contactModalOpen, setContactModalOpen] = React.useState(false);
    
    return (
        <>
            <div className="landing-page-body">
                {/* ── Header ── */}
                <header className="header">
                    <div className="logo">
                        🧭 NoorNavigator
                    </div>
                    <nav className="nav-links">
                        <a href="#" className="nav-active-home">Home</a>
                        <Link href="/about">About</Link>
                        <Link href="/partners">Partners</Link>
                        <a href="#" onClick={(e) => { e.preventDefault(); setContactModalOpen(true); }}>Contact Us</a>
                    </nav>
                    <div className="auth-buttons">
                        <Link href="/signin" className="btn-login">Sign In</Link>
                        <Link href="/signup" className="btn-signup">Get Started</Link>
                    </div>
                </header>

                {/* ── Hero ── */}
                <section className="hero">
                    <div className="hero-content">
                        <h1 className="animate-fade-in">NoorNavigator</h1>
                        <p className="animate-fade-in">Your complete Islamic community guide. Discover prayer times, find halal businesses, and connect with Muslims in your area - all in one powerful platform.</p>
                        <div className="hero-cta">
                            <a href="#join" className="btn-primary">Start Your Journey</a>
                            <a href="#preview" className="btn-secondary">See How It Works</a>
                        </div>
                    </div>
                </section>

                {/* ── Features ── */}
                <section className="features-standalone" id="features">
                    <div className="features-standalone-inner">
                        <div className="features-standalone-header">
                            <div className="features-badge">What We Offer</div>
                            <h2>Everything You Need as a Practicing Muslim</h2>
                            <p>One platform, built with intention - designed to make Islamic living simpler and more connected.</p>
                        </div>
                        <div className="features-grid">
                            <div className="feature-card">
                                <div className="feature-card-icon">🗺️</div>
                                <h3>Smart Location Discovery</h3>
                                <p>Find mosques, halal restaurants, and Islamic businesses near you with community-verified listings.</p>
                            </div>
                            <div className="feature-card">
                                <div className="feature-card-icon">⏰</div>
                                <h3>Precise Prayer Tracking</h3>
                                <p>Never miss a prayer with location-based timing, customisable notifications, and support for different calculation methods.</p>
                            </div>
                            <div className="feature-card">
                                <div className="feature-card-icon">🤝</div>
                                <h3>Community Connection</h3>
                                <p>Connect with local Muslims, find mentors, join study groups, and build meaningful relationships in your community.</p>
                            </div>
                            <div className="feature-card">
                                <div className="feature-card-icon">📱</div>
                                <h3>All-in-One Platform</h3>
                                <p>Everything you need as a practicing Muslim in one beautifully designed app - from prayer times to community events.</p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* ── App Preview ── */}
                <section className="app-preview" id="preview">
                    <h2>Experience NoorNavigator</h2>
                    <p className="app-preview-subtitle">See how our intuitive platform makes Islamic community navigation effortless and meaningful</p>
                    
                    <div className="app-showcase">
                        <div className="phone-mockup">
                            <div className="phone-frame">
                                {/* Dynamic Island notch */}
                                <div className="phone-notch"></div>
                                <div className="phone-screen">
                                    {/* Status Bar */}
                                    <div className="phone-status-bar">
                                        <span className="phone-status-time">9:41</span>
                                        <span className="phone-status-icons">▲ 📶 🔋</span>
                                    </div>
                                    <div className="app-interface">
                                        <div className="app-header">
                                            <div className="app-title">Discover</div>
                                            <div className="nearby-btn">📍 Nearby</div>
                                        </div>
                                        
                                        <div className="search-bar">
                                            🔍 Search by name or address...
                                        </div>
                                        
                                        <div className="prayer-times-widget">
                                            <div className="prayer-times-header">
                                                <div className="prayer-times-title">Prayer Times</div>
                                                <div className="location">📍 Sydney</div>
                                            </div>
                                            <div className="prayer-grid">
                                                <div className="prayer-item">
                                                    <div className="prayer-name">Fajr</div>
                                                    <div className="prayer-time">5:11 AM</div>
                                                </div>
                                                <div className="prayer-item">
                                                    <div className="prayer-name">Dhuhr</div>
                                                    <div className="prayer-time">12:00 PM</div>
                                                </div>
                                                <div className="prayer-item current">
                                                    <div className="prayer-name">Asr</div>
                                                    <div className="prayer-time">3:01 PM</div>
                                                </div>
                                                <div className="prayer-item">
                                                    <div className="prayer-name">Maghrib</div>
                                                    <div className="prayer-time">5:25 PM</div>
                                                </div>
                                                <div className="prayer-item">
                                                    <div className="prayer-name">Isha</div>
                                                    <div className="prayer-time">6:44 PM</div>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <div className="filter-tabs">
                                            <div className="filter-tab active">All</div>
                                            <div className="filter-tab vertical"><span>🍽️</span><span>Food</span></div>
                                            <div className="filter-tab vertical"><span>🕌</span><span>Prayer</span></div>
                                            <div className="filter-tab vertical"><span>🏢</span><span>Business</span></div>
                                        </div>
                                    </div>
                                    {/* Home Indicator */}
                                    <div className="phone-home-indicator"></div>
                                </div>
                            </div>
                        </div>

                        <div className="preview-tagline">
                            <div className="preview-tagline-badge">Live Preview</div>
                            <h3>A seamless experience from the first tap</h3>
                            <p>
                                NoorNavigator puts prayer times, halal discovery, and community connection all in one place.
                                Designed for Muslims, by a Muslim who understands what matters.
                            </p>
                            <Link href="/signup" className="btn-preview-cta">Try It Free →</Link>
                        </div>
                    </div>
                </section>
                
                {/* ── Join ── */}
                <section className="test-drive" id="join">
                    <h2>Join NoorNavigator Today</h2>
                    <p className="test-drive-subtitle">Be among the first to experience the future of Islamic community connection.</p>
                    <SignUpForm />
                </section>

                {/* ── Modals ── */}
                {loginModalOpen && <div id="loginModal" className="modal" style={{display: 'flex'}}>
                    <div className="modal-content">
                        <span className="close" id="closeLogin" onClick={() => setLoginModalOpen(false)}>&times;</span>
                        <AuthGate />
                    </div>
                </div>}

                {contactModalOpen && <div id="contactModal" className="modal" style={{display: 'flex'}}>
                    <div className="modal-content">
                        <span className="close" id="closeContact" onClick={() => setContactModalOpen(false)}>&times;</span>
                        <h3>Contact Us</h3>
                        <p style={{ textAlign: 'center', lineHeight: 1.6 }}>
                            Email <a href="mailto:noornavigator@outlook.com" style={{ color: 'hsl(120 27% 65%)' }}>noornavigator@outlook.com</a> for any enquiries e.g. partnerships, account deletion, etc.</p>
                    </div>
                </div>}

                {/* ── Footer ── */}
                <footer className="footer">
                    <div className="footer-content">
                        <div className="footer-logo">🧭 NoorNavigator</div>
                        <div className="flex justify-center gap-6 mb-8 text-sm opacity-80">
                           <Link href="/partners" className="hover:text-primary transition-colors flex items-center gap-1">
                             <Handshake className="w-4 h-4" /> Partner With Us
                           </Link>
                           <Link href="/privacy-policy" className="hover:text-primary transition-colors">Privacy Policy</Link>
                        </div>
                        <p>© 2025 NoorNavigator. All rights reserved. | Email <a href="mailto:noornavigator@outlook.com" className="hover:underline" style={{color: 'hsl(120 27% 65%)'}}>noornavigator@outlook.com</a> for any enquiries e.g. partnerships, account deletion, etc.</p>
                        <div className="flex justify-center gap-6 mt-4">
                           <a href="https://www.instagram.com/noornavigatorau/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:opacity-80 transition-opacity" style={{color: 'hsl(120 27% 65%)'}}>
                             <Instagram className="w-5 h-5" />
                             <span className="text-sm">noornavigatorau</span>
                           </a>
                           <a href="https://www.linkedin.com/company/noornavigator" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:opacity-80 transition-opacity" style={{color: 'hsl(120 27% 65%)'}}>
                             <Linkedin className="w-5 h-5" />
                             <span className="text-sm">NoorNavigator</span>
                           </a>
                        </div>
                    </div>
                </footer>
            </div>
        </>
    );
}
